import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@geiertime/ui'
import { checkoutApi } from '../api/client'

/**
 * Email validation regex.
 * Validates standard email format.
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Disposable email domains to block.
 * Common throwaway email providers.
 */
const DISPOSABLE_DOMAINS = [
  'tempmail.com',
  'throwaway.com',
  'mailinator.com',
  'guerrillamail.com',
  '10minutemail.com',
  'yopmail.com',
]

interface Plan {
  id: string
  name: string
  description: string
  price: number
  billingPeriod: string
  licenseType: string
  features: string[]
}

/**
 * Validates email format and checks against disposable domains.
 */
function validateEmail(email: string): string | null {
  if (!email.trim()) {
    return 'L\'email è obbligatoria'
  }
  if (!EMAIL_REGEX.test(email)) {
    return 'Inserisci un\'email valida'
  }
  const domain = email.split('@')[1]?.toLowerCase()
  if (domain && DISPOSABLE_DOMAINS.includes(domain)) {
    return 'Non sono ammessi indirizzi email temporanei'
  }
  return null
}

/**
 * Checkout page route.
 * Displays payment form for the selected plan.
 * Supports Stripe and Binance Pay providers.
 */
export const Route = createFileRoute('/checkout/$planId')({
  component: CheckoutPage,
})

function CheckoutPage() {
  const { planId } = Route.useParams()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [provider, setProvider] = useState<'stripe' | 'binance'>('stripe')
  const [emailError, setEmailError] = useState<string | null>(null)

  // Fetch all plans to find the selected one
  const {
    data: plansData,
    isLoading: plansLoading,
    error: plansError,
  } = useQuery({
    queryKey: ['plans'],
    queryFn: () => checkoutApi.getPlans(),
  })

  const plan: Plan | undefined = plansData?.data?.find(
    (p: Plan) => p.id === planId
  )

  // Checkout mutation
  const checkoutMutation = useMutation({
    mutationFn: (data: {
      planId: string
      email: string
      licenseType: string
      provider: 'stripe' | 'binance'
      returnUrl: string
    }) => checkoutApi.createCheckout(data),
    onSuccess: (response) => {
      const { redirectUrl } = response.data
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
    },
    onError: (error) => {
      console.error('Checkout error:', error)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const error = validateEmail(email)
    if (error) {
      setEmailError(error)
      return
    }
    setEmailError(null)

    if (!plan) return

    const returnUrl = `${window.location.origin}/checkout/success`

    checkoutMutation.mutate({
      planId,
      email,
      licenseType: plan.licenseType,
      provider,
      returnUrl,
    })
  }

  if (plansLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="mx-auto max-w-md space-y-6">
          <div className="h-8 bg-muted rounded animate-pulse" />
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
        </div>
      </div>
    )
  }

  if (plansError || !plan) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="mx-auto max-w-md text-center space-y-4">
          <h1 className="text-2xl font-bold">Piano non trovato</h1>
          <p className="text-muted-foreground">
            Il piano selezionato non è disponibile.
          </p>
          <Button onClick={() => navigate({ to: '/' })} className="w-full">
            Torna alla Home
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="mx-auto max-w-md space-y-6">
        <h1 className="text-3xl font-bold text-center">Checkout</h1>

        {/* Plan Summary */}
        <Card>
          <CardHeader>
            <CardTitle>{plan.name}</CardTitle>
            <CardDescription>{plan.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold">
                {new Intl.NumberFormat('it-IT', {
                  style: 'currency',
                  currency: 'EUR',
                }).format(plan.price)}
              </span>
              <span className="text-muted-foreground">
                /{plan.billingPeriod === 'monthly' ? 'mese' : 'anno'}
              </span>
            </div>
            <ul className="space-y-2">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="text-sm text-muted-foreground">
                  • {feature}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Checkout Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="il-tuo@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (emailError) setEmailError(null)
                  }}
                  aria-invalid={emailError ? 'true' : 'false'}
                  aria-describedby={emailError ? 'email-error' : undefined}
                  required
                />
                {emailError && (
                  <p id="email-error" className="text-sm text-destructive">
                    {emailError}
                  </p>
                )}
              </div>

              {/* Provider Selection */}
              <div className="space-y-2">
                <Label>Metodo di Pagamento</Label>
                <Select
                  value={provider}
                  onValueChange={(value: 'stripe' | 'binance') =>
                    setProvider(value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona un metodo di pagamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">
                      💳 Carta di Credito (Stripe)
                    </SelectItem>
                    <SelectItem value="binance">
                      🅱️ Binance Pay
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={checkoutMutation.isPending}
          >
            {checkoutMutation.isPending
              ? 'Elaborazione...'
              : 'Procedi al Pagamento'}
          </Button>

          {checkoutMutation.isError && (
            <p className="text-sm text-destructive text-center">
              Si è verificato un errore durante il checkout. Riprova.
            </p>
          )}
        </form>
      </div>
    </div>
  )
}
